/**
 * Weather System - Day/night cycle, rain, fog, snow, and storm effects
 * Renders atmospheric overlays on top of the game world using Canvas2D
 */

import { getFogPatchCanvas } from '@client/core/GradientCache';

export type WeatherType = 'clear' | 'rain' | 'fog' | 'snow' | 'storm';
export type TimeOfDay = 'dawn' | 'day' | 'dusk' | 'night';

/** Individual weather particle (rain drop, snowflake, etc.) */
interface WeatherParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  /** For rain splash or snowflake wobble phase */
  phase: number;
  /** Whether this particle is currently active (used for object pooling) */
  active: boolean;
  /** Pre-assigned alpha bucket index for snow rendering (0, 1, or 2) */
  alphaBucket: number;
}

/** Fog patch for the fog effect */
interface FogPatch {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  speed: number;
  phase: number;
}

export class WeatherSystem {
  // Day/night cycle
  private dayTime: number = 0.35; // Start at mid-morning (0-1 representing full day cycle)
  private dayDuration: number = 300; // 5 minutes real time = 1 game day

  // Weather state
  private currentWeather: WeatherType = 'clear';
  private weatherIntensity: number = 0; // 0-1
  private weatherTransition: number = 0; // 0-1 transition progress
  private targetWeather: WeatherType = 'clear';
  private targetIntensity: number = 0;
  private transitionSpeed: number = 0.3; // Transitions take ~3 seconds

  // Particles for rain/snow (object pool — particles are reused, not reallocated)
  private particles: WeatherParticle[] = [];
  private activeCount: number = 0;
  private fogPatches: FogPatch[] = [];

  // Lightning state (for storms)
  private lightningTimer: number = 0;
  private lightningFlash: number = 0; // Current flash intensity 0-1
  private screenShake: { x: number; y: number } = { x: 0, y: 0 };

  // Zone overrides: weather type + fixed intensity
  private zoneWeather: Record<string, { type: WeatherType; intensity: number }> = {
    // Act 1: Progressive atmospheric escalation
    'otag_koyu':      { type: 'clear', intensity: 0 },
    'karanlik_orman':  { type: 'rain', intensity: 0.4 },     // Light rain - ominous
    'ruh_batakligi':   { type: 'fog', intensity: 0.7 },      // Thick fog - eerie
    'erlik_tapinagi':  { type: 'storm', intensity: 0.6 },    // Thunder/lightning - dramatic
    // Act 2
    'ergenekon_girisi': { type: 'clear', intensity: 0 },
    'firtina_ovasi':    { type: 'storm', intensity: 0.8 },
    'ates_magarasi':    { type: 'fog', intensity: 0.5 },     // Smoke/heat haze
    'yersub_tahti':     { type: 'rain', intensity: 0.6 },
    // Act 3
    'karanlik_tunel':  { type: 'fog', intensity: 0.8 },      // Dense underground fog
    'olum_nehri':      { type: 'rain', intensity: 0.7 },
    'kemik_ormani':    { type: 'fog', intensity: 0.6 },
    'lanetli_saray':   { type: 'storm', intensity: 0.7 },
    // Act 4
    'erlik_kapisi':     { type: 'storm', intensity: 0.8 },
    'cehennem_bahcesi': { type: 'storm', intensity: 0.9 },
    'taht_odasi':       { type: 'storm', intensity: 1.0 },   // Maximum intensity for final boss
  };

  constructor() {
    this.initFogPatches();
  }

  /** Initialize fog patch entities */
  private initFogPatches(): void {
    this.fogPatches = [];
    for (let i = 0; i < 12; i++) {
      this.fogPatches.push({
        x: Math.random() * 2000 - 500,
        y: Math.random() * 1200 - 200,
        radius: 80 + Math.random() * 120,
        alpha: 0.05 + Math.random() * 0.1,
        speed: 5 + Math.random() * 15, // Parallax: different speeds
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  /** Set the zone and apply zone-specific weather override */
  setZone(zoneId: string): void {
    const zoneOverride = this.zoneWeather[zoneId];
    if (zoneOverride) {
      // Add slight random variation to intensity (±10%)
      const variation = (Math.random() - 0.5) * 0.2;
      const intensity = Math.max(0, Math.min(1, zoneOverride.intensity + variation));
      this.setWeather(zoneOverride.type, intensity);
    } else {
      this.setWeather('clear', 0);
    }
  }

  /** Transition to a new weather type */
  setWeather(weather: WeatherType, intensity: number = 1): void {
    if (weather === this.currentWeather && Math.abs(intensity - this.weatherIntensity) < 0.1) return;
    this.targetWeather = weather;
    this.targetIntensity = Math.max(0, Math.min(1, intensity));
    this.weatherTransition = 0;
  }

  /** Get the current time of day based on dayTime */
  getTimeOfDay(): TimeOfDay {
    const t = this.dayTime;
    if (t >= 0.2 && t < 0.3) return 'dawn';
    if (t >= 0.3 && t < 0.7) return 'day';
    if (t >= 0.7 && t < 0.8) return 'dusk';
    return 'night';
  }

  /** Returns the ambient tint color for the current time of day */
  getAmbientColor(): string {
    const tod = this.getTimeOfDay();
    switch (tod) {
      case 'dawn': return '#ff8c42'; // Warm orange
      case 'day': return '#000000';  // No tint
      case 'dusk': return '#8b3a62'; // Red/purple
      case 'night': return '#1a2a5a'; // Deep blue
    }
  }

  /** Returns darkness level (overlay alpha) for current time of day */
  getAmbientAlpha(): number {
    const t = this.dayTime;

    // Dawn (0.2-0.3): fade from 0.3 to 0.1
    if (t >= 0.2 && t < 0.3) {
      const progress = (t - 0.2) / 0.1;
      return 0.3 - progress * 0.2;
    }
    // Day (0.3-0.7): alpha 0
    if (t >= 0.3 && t < 0.7) {
      // Slight adjustment at edges
      if (t < 0.35) return 0.1 * (1 - (t - 0.3) / 0.05);
      if (t > 0.65) return 0.1 * ((t - 0.65) / 0.05);
      return 0;
    }
    // Dusk (0.7-0.8): fade from 0.1 to 0.3
    if (t >= 0.7 && t < 0.8) {
      const progress = (t - 0.7) / 0.1;
      return 0.1 + progress * 0.2;
    }
    // Night (0.8-1.0, 0-0.2): alpha 0.3
    return 0.3;
  }

  /** Get screen shake offsets (for storm thunder) */
  getScreenShake(): { x: number; y: number } {
    return this.screenShake;
  }

  /** Main update tick */
  update(deltaTime: number): void {
    // Advance day/night cycle
    this.dayTime += deltaTime / this.dayDuration;
    if (this.dayTime >= 1) this.dayTime -= 1;

    // Transition weather
    if (this.targetWeather !== this.currentWeather || Math.abs(this.targetIntensity - this.weatherIntensity) > 0.01) {
      this.weatherTransition += deltaTime * this.transitionSpeed;
      if (this.weatherTransition >= 1) {
        this.weatherTransition = 1;
        this.currentWeather = this.targetWeather;
        this.weatherIntensity = this.targetIntensity;
      } else {
        // Lerp intensity
        this.weatherIntensity += (this.targetIntensity - this.weatherIntensity) * deltaTime * this.transitionSpeed;
        // Switch type at halfway
        if (this.weatherTransition > 0.5) {
          this.currentWeather = this.targetWeather;
        }
      }
    }

    // Skip particle updates when weather is clear or intensity is negligible
    if (this.currentWeather === 'clear' || this.weatherIntensity < 0.01) {
      // Deactivate any lingering particles
      this.deactivateExcess(0);
      // Still decay screen shake
      this.screenShake.x *= 0.85;
      this.screenShake.y *= 0.85;
      if (Math.abs(this.screenShake.x) < 0.1) this.screenShake.x = 0;
      if (Math.abs(this.screenShake.y) < 0.1) this.screenShake.y = 0;
      return;
    }

    // Update particles based on weather type
    switch (this.currentWeather) {
      case 'rain':
        this.updateRainParticles(deltaTime);
        break;
      case 'snow':
        this.updateSnowParticles(deltaTime);
        break;
      case 'storm':
        this.updateRainParticles(deltaTime);
        this.updateLightning(deltaTime);
        break;
      case 'fog':
        this.updateFog(deltaTime);
        break;
    }

    // Decay screen shake
    this.screenShake.x *= 0.85;
    this.screenShake.y *= 0.85;
    if (Math.abs(this.screenShake.x) < 0.1) this.screenShake.x = 0;
    if (Math.abs(this.screenShake.y) < 0.1) this.screenShake.y = 0;
  }

  /** Reset a particle's properties for rain */
  private resetRainParticle(p: WeatherParticle): void {
    p.x = Math.random() * 2000 - 200;
    p.y = -10 - Math.random() * 100;
    p.vx = -30 - Math.random() * 20;
    p.vy = 600 + Math.random() * 200;
    p.size = 1 + Math.random() * 1.5;
    p.alpha = 0.3 + Math.random() * 0.4;
    p.phase = 0;
    p.active = true;
    p.alphaBucket = 0;
  }

  /** Acquire a particle from the pool, or create a new one if none inactive */
  private acquireParticle(): WeatherParticle {
    // Look for an inactive particle in the pool
    for (let i = this.activeCount; i < this.particles.length; i++) {
      if (!this.particles[i].active) {
        // Swap with first inactive slot for contiguous active region
        const p = this.particles[i];
        this.particles[i] = this.particles[this.activeCount];
        this.particles[this.activeCount] = p;
        this.activeCount++;
        return p;
      }
    }
    // No inactive particle available; create a new one
    const p: WeatherParticle = {
      x: 0, y: 0, vx: 0, vy: 0,
      size: 0, alpha: 0, phase: 0,
      active: true, alphaBucket: 0,
    };
    this.particles.push(p);
    this.activeCount++;
    return p;
  }

  /** Deactivate excess particles */
  private deactivateExcess(targetCount: number): void {
    while (this.activeCount > targetCount) {
      this.activeCount--;
      this.particles[this.activeCount].active = false;
    }
  }

  /** Update rain particles */
  private updateRainParticles(deltaTime: number): void {
    const targetCount = Math.round(200 * this.weatherIntensity);

    // Spawn new particles by reusing pooled objects
    while (this.activeCount < targetCount) {
      const p = this.acquireParticle();
      this.resetRainParticle(p);
    }

    // Deactivate excess
    this.deactivateExcess(targetCount);

    // Move particles
    for (let i = this.activeCount - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;

      // Respawn at top if past bottom (ground area is bottom 1/3)
      if (p.y > 800) {
        p.x = Math.random() * 2000 - 200;
        p.y = -10 - Math.random() * 50;
        p.phase = 1; // Mark as splash
      }
    }
  }

  /** Reset a particle's properties for snow */
  private resetSnowParticle(p: WeatherParticle): void {
    p.x = Math.random() * 2000 - 200;
    p.y = -10 - Math.random() * 100;
    p.vx = 0;
    p.vy = 30 + Math.random() * 40;
    p.size = 2 + Math.random() * 3;
    p.alpha = 0.4 + Math.random() * 0.3;
    p.phase = Math.random() * Math.PI * 2;
    p.active = true;
    // Pre-assign alpha bucket: 0 = [0, 0.33), 1 = [0.33, 0.66), 2 = [0.66, 1.0)
    p.alphaBucket = Math.min(Math.floor(p.alpha * 3), 2);
  }

  /** Update snow particles */
  private updateSnowParticles(deltaTime: number): void {
    const targetCount = Math.round(100 * this.weatherIntensity);

    while (this.activeCount < targetCount) {
      const p = this.acquireParticle();
      this.resetSnowParticle(p);
    }

    this.deactivateExcess(targetCount);

    for (let i = this.activeCount - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.phase += deltaTime * 2;
      // Wobble on x axis
      p.x += Math.sin(p.phase) * 20 * deltaTime;
      p.y += p.vy * deltaTime;

      if (p.y > 800) {
        p.x = Math.random() * 2000 - 200;
        p.y = -10 - Math.random() * 50;
      }
    }
  }

  /** Update fog drift */
  private updateFog(deltaTime: number): void {
    for (const patch of this.fogPatches) {
      patch.phase += deltaTime * 0.5;
      patch.x += Math.sin(patch.phase) * patch.speed * deltaTime;

      // Wrap fog patches
      if (patch.x > 2200) patch.x = -300;
      if (patch.x < -400) patch.x = 2100;
    }
  }

  /** Update lightning for storms */
  private updateLightning(deltaTime: number): void {
    // Decay flash
    if (this.lightningFlash > 0) {
      this.lightningFlash -= deltaTime * 10; // Flash lasts ~0.1s
      if (this.lightningFlash < 0) this.lightningFlash = 0;
    }

    // Timer to next lightning
    this.lightningTimer -= deltaTime;
    if (this.lightningTimer <= 0) {
      // Flash!
      this.lightningFlash = 0.8 + Math.random() * 0.2;
      // Thunder screen shake (delayed feel from single frame)
      this.screenShake.x = (Math.random() - 0.5) * 8;
      this.screenShake.y = (Math.random() - 0.5) * 6;
      // Next lightning in 10-20 seconds
      this.lightningTimer = 10 + Math.random() * 10;
    }
  }

  /** Render all weather effects */
  render(
    ctx: CanvasRenderingContext2D,
    screenWidth: number,
    screenHeight: number,
    _cameraX: number,
    _cameraY: number
  ): void {
    // 1. Day/night ambient overlay
    this.renderAmbientOverlay(ctx, screenWidth, screenHeight);

    // 2. Weather-specific effects
    if (this.weatherIntensity > 0.01) {
      switch (this.currentWeather) {
        case 'rain':
          this.renderRain(ctx, screenWidth, screenHeight);
          this.renderRainDarkening(ctx, screenWidth, screenHeight);
          break;
        case 'snow':
          this.renderSnow(ctx);
          break;
        case 'fog':
          this.renderFog(ctx, screenWidth, screenHeight);
          break;
        case 'storm':
          this.renderRain(ctx, screenWidth, screenHeight);
          this.renderRainDarkening(ctx, screenWidth, screenHeight);
          this.renderLightningFlash(ctx, screenWidth, screenHeight);
          break;
      }
    }
  }

  /** Render the day/night ambient color overlay */
  private renderAmbientOverlay(
    ctx: CanvasRenderingContext2D,
    screenWidth: number,
    screenHeight: number
  ): void {
    const alpha = this.getAmbientAlpha();
    if (alpha <= 0.001) return;

    const color = this.getAmbientColor();
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha;
    ctx.fillRect(0, 0, screenWidth, screenHeight);
    ctx.globalAlpha = 1;
  }

  /** Render rain particles as thin white diagonal lines (batched, single-pass) */
  private renderRain(
    ctx: CanvasRenderingContext2D,
    _screenWidth: number,
    screenHeight: number
  ): void {
    const intensity = this.weatherIntensity;
    const splashThreshold = screenHeight * 0.7;

    // Batch all rain streaks into a single path, collecting splash candidates in the same loop
    ctx.strokeStyle = 'rgba(200, 210, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.globalAlpha = intensity;
    ctx.beginPath();

    // Reusable array to avoid allocations — we store indices of splash particles
    let splashCount = 0;

    for (let i = 0; i < this.activeCount; i++) {
      const p = this.particles[i];
      if (p.y < 0 || p.y > screenHeight + 10) continue;
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x - p.vx * 0.015, p.y - p.vy * 0.015);

      // Mark splash inline — reuse phase to avoid second loop
      if (p.y > splashThreshold && p.phase > 0) {
        splashCount++;
      }
    }
    ctx.stroke();

    // Batch all splash effects into a single path (only if any splashes were found)
    if (splashCount > 0) {
      ctx.fillStyle = 'rgba(200, 210, 255, 0.4)';
      ctx.globalAlpha = intensity * 0.5;
      ctx.beginPath();
      for (let i = 0; i < this.activeCount; i++) {
        const p = this.particles[i];
        if (p.y > splashThreshold && p.phase > 0) {
          ctx.moveTo(p.x + 2, p.y);
          ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        }
      }
      ctx.fill();
    }

    ctx.globalAlpha = 1;
  }

  /** Darken the screen slightly during rain */
  private renderRainDarkening(
    ctx: CanvasRenderingContext2D,
    screenWidth: number,
    screenHeight: number
  ): void {
    const darkenAlpha = 0.1 * this.weatherIntensity;
    if (darkenAlpha > 0.001) {
      ctx.fillStyle = '#1a2030';
      ctx.globalAlpha = darkenAlpha;
      ctx.fillRect(0, 0, screenWidth, screenHeight);
      ctx.globalAlpha = 1;
    }
  }

  /** Render snow particles (batched by pre-assigned alpha bucket to minimize state changes) */
  private renderSnow(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#ffffff';
    const intensity = this.weatherIntensity;

    // Pre-computed bucket alpha values: midpoint of each range * intensity
    const bucketAlphas = [
      (1 / 6) * intensity,  // Bucket 0: alpha 0.0-0.33, midpoint 0.167
      (1 / 2) * intensity,  // Bucket 1: alpha 0.33-0.66, midpoint 0.5
      (5 / 6) * intensity,  // Bucket 2: alpha 0.66-1.0, midpoint 0.833
    ];

    // Single pass: build 3 sub-paths using the pre-assigned alphaBucket property
    // We render one bucket at a time but only iterate active particles once per bucket
    for (let bucket = 0; bucket < 3; bucket++) {
      ctx.globalAlpha = bucketAlphas[bucket];
      ctx.beginPath();
      let hasParticles = false;

      for (let i = 0; i < this.activeCount; i++) {
        const p = this.particles[i];
        if (p.alphaBucket !== bucket) continue;
        if (p.y < -10 || p.y > 900) continue;

        ctx.moveTo(p.x + p.size, p.y);
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        hasParticles = true;
      }

      if (hasParticles) {
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;
  }

  /** Render fog patches as large translucent circles (cached offscreen canvases) */
  private renderFog(
    ctx: CanvasRenderingContext2D,
    screenWidth: number,
    screenHeight: number
  ): void {
    for (const patch of this.fogPatches) {
      // Skip offscreen patches
      if (patch.x + patch.radius < 0 || patch.x - patch.radius > screenWidth) continue;
      if (patch.y + patch.radius < 0 || patch.y - patch.radius > screenHeight) continue;

      const fogAlpha = patch.alpha * this.weatherIntensity;
      const fogCanvas = getFogPatchCanvas(patch.radius, fogAlpha, 1);
      ctx.globalAlpha = fogAlpha;
      ctx.drawImage(fogCanvas, patch.x - fogCanvas.width / 2, patch.y - fogCanvas.height / 2);
    }
    ctx.globalAlpha = 1;
  }

  /** Render lightning flash (white screen overlay) */
  private renderLightningFlash(
    ctx: CanvasRenderingContext2D,
    screenWidth: number,
    screenHeight: number
  ): void {
    if (this.lightningFlash <= 0.01) return;

    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = this.lightningFlash * this.weatherIntensity;
    ctx.fillRect(0, 0, screenWidth, screenHeight);
    ctx.globalAlpha = 1;
  }
}
